import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { Platform } from '@root/entities/platform'
import { PlatformIcon } from '@root/entities/platform/ui/platform-icon'

type Props = {
  value: Platform.Platform
  onChange: (platform: Platform.Platform) => void
}

export const NotificationCenterPlatforms: React.FC<Props> = ({
  value = Platform.Terminal,
  onChange,
}) => {
  const { t } = useTranslation()

  const handleSelect = (item: Platform.Platform) => {
    onChange(item)
  }

  return (
    <>
      <PlatformTitle>
        <SeparatorStart />
        <Heading>{t('platform.deluge_apps')}</Heading>
        <SeparatorEnd />
      </PlatformTitle>
      <PlatformsList>
        {Platform.platforms.map((item, index) => (
          <PlatformItem
            key={index}
            aria-checked={value === item}
            onClick={() => handleSelect(item)}
          >
            <PlatformIcon platform={item} size={32} frameSize={50} />
            <Label>{Platform.PLATFORM_LABEL_MAP[item]}</Label>
          </PlatformItem>
        ))}
      </PlatformsList>
    </>
  )
}

const PlatformItem = styled.div`
  color: var(--text-12);
  display: grid;
  position: relative;
  isolation: isolate;
  overflow: hidden;

  border-radius: 10px;
  padding: 10px 8px;
  gap: 6px;

  border: 0.6px solid var(--backgroundA-5);
  backdrop-filter: blur(1.5px);

  &[aria-checked='true'] {
    border-color: transparent;
    background: radial-gradient(
      107.31% 377.35% at 135.68% -217.8%,
      var(--primaryA-5) 50.76%,
      var(--primaryA-3) 100%
    );

    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 0.6px;
      pointer-events: none;
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }

    &::before {
      background: radial-gradient(
        150% 300% at 115% -160%,
        var(--primaryA-8) 0%,
        var(--primaryA-1) 100%
      );
    }

    &::after {
      background: radial-gradient(
        80% 120% at -3% -18%,
        var(--primaryA-5) 0%,
        var(--primaryA-1) 100%
      );
    }
  }
`

const PlatformTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  justify-content: start;
  justify-items: start;

  gap: 8px;
  margin-top: 4px;
`

const Heading = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  color: var(--text-11);
`

const SeparatorStart = styled.div`
  width: 100%;
  height: 1px;

  opacity: 0.2;
  background: linear-gradient(90deg, transparent 0%, var(--text-12) 100%);
`

const SeparatorEnd = styled.div`
  width: 100%;
  height: 1px;
  opacity: 0.2;
  background: linear-gradient(-90deg, transparent 0%, var(--text-12) 100%);
`

const Label = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'span',
})`
  color: var(--text-11);

  text-align: center;
  font-size: 8px;

  ${PlatformItem}[aria-checked='true'] & {
    color: var(--text-12);
  }
`

const PlatformsList = styled.div`
  display: grid;
  justify-content: start;
  justify-items: start;
  grid-auto-flow: column;

  gap: 6px;
`
